/**
 * FieldModeAutoApproveSettings - Kişi bazlı: hibrit çalışanların saha modu geçiş
 * taleplerinin onay beklemeden otomatik uygulanmasını (sadece bildirim) yönetir.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Switch, Spin, message, Avatar, Alert } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import userService from '../../../../services/user/user.service';
import { User } from '../../../../types/user.types';
import { getFileUrl } from '../../../../utils/file.utils';
import { handleApiError } from '../../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';

const FieldModeAutoApproveSettings: React.FC = () => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const result = await userService.getHybridEmployees();
      setEmployees(result);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleToggle = async (employee: User, checked: boolean) => {
    setTogglingId(employee.id);
    try {
      await userService.setFieldModeAutoApprove(employee.id, checked);
      setEmployees((prev) =>
        prev.map((e) => (e.id === employee.id ? { ...e, fieldModeAutoApprove: checked } : e))
      );
      message.success(
        checked
          ? `${employee.firstName} ${employee.lastName} için saha modu talepleri artık otomatik onaylanacak.`
          : `${employee.firstName} ${employee.lastName} için saha modu talepleri yine onay bekleyecek.`
      );
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setTogglingId(null);
    }
  };

  const getProfilePicture = (employee: User) => {
    if (employee.profilePicture && employee.profilePicture.trim() !== '') {
      return getFileUrl(employee.profilePicture);
    }
    return undefined;
  };

  return (
    <div className="card border rounded-lg">
      <div className="card-header">
        <h5 className="mb-1">Kişi Bazlı Otomatik Onay İstisnaları</h5>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Açık olan çalışanların saha/ofis geçiş talepleri onay beklemeden uygulanır, size sadece bilgilendirme bildirimi gelir.
        </p>
      </div>
      <div className="card-body">
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message='Departman ayarlarında saha modu talebi "Otomatik onay" olarak seçilen departmanlardaki tüm hibrit çalışanların talepleri zaten otomatik onaylanır. Buradaki ayarlar, onay gerektiren departmanlardaki çalışanlar için kişi bazlı istisnalardır.'
        />
        {loading ? (
          <div className="text-center py-4">
            <Spin />
          </div>
        ) : employees.length === 0 ? (
          <p className="text-muted mb-0">Hibrit çalışan bulunmuyor.</p>
        ) : (
          <div className="d-flex flex-column gap-3">
            {employees.map((employee) => (
              <div
                key={employee.id}
                className="d-flex align-items-center justify-content-between border-bottom pb-3"
              >
                <div className="d-flex align-items-center gap-3">
                  <Avatar
                    size={40}
                    src={getProfilePicture(employee)}
                    icon={!getProfilePicture(employee) ? <UserOutlined /> : undefined}
                  >
                    {!getProfilePicture(employee) && `${employee.firstName[0]}${employee.lastName[0]}`}
                  </Avatar>
                  <div>
                    <div className="fw-medium">
                      {employee.firstName} {employee.lastName}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                      {employee.primaryDepartment?.name || 'Departman Yok'}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={!!employee.fieldModeAutoApprove}
                  loading={togglingId === employee.id}
                  onChange={(checked) => handleToggle(employee, checked)}
                  checkedChildren="Otomatik"
                  unCheckedChildren="Onaylı"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldModeAutoApproveSettings;
